'use client';

import { PulseProvider } from '@cineamo/pulse/src/react/PulseProvider';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
import type { AbstractIntlMessages } from 'next-intl';
import { NextIntlClientProvider } from 'next-intl';
import React, { useMemo, useState } from 'react';
import { ToastContainer } from 'react-toastify';

import { AdvertisementProvider } from '@/components/advertisement/AdvertisementProvider';
import NewsletterSubscribeModal from '@/components/newsletter/NewsletterSubscribeModal';

import { CinemaRoomDto } from '@cineamo/api/models/cinema-room/CinemaRoomDto.types';
import { ShowingTagDto } from '@cineamo/api/models/showing-tag/ShowingTagDto.types';

import { DomainConfigurationDto } from '@/types/DomainConfigurationDto';

import { GA4_EVENT_MAP, MATOMO_EVENT_MAP, META_PIXEL_EVENT_MAP, toSnakeCase } from '@/constants/analytics-events';

import { COUNTRY_TIMEZONE_MAPPING } from '@cineamo/ui/src/helpers/locale-helper';

import AnalyticsProvider from '@cineamo/composables/src/providers/analyticsProvider';

import { CinemaRoomProvider } from '@/providers/CinemaRoomProvider';
import { DomainConfigurationProvider } from '@/providers/DomainConfigurationProvider';
import NewsletterModalProvider from '@/providers/NewsletterModalProvider';
import { ShowingTagProvider } from '@/providers/ShowingTagProvider';
import { UserAgentProvider } from '@/providers/UserAgentProvider';

declare global {
    interface Window {
        gtag?: (...args: unknown[]) => void;
    }
}

type ProvidersProps = {
    locale: string;
    messages: AbstractIntlMessages;
    domainConfiguration: DomainConfigurationDto;
    userAgent?: string;
    cinemaRooms: CinemaRoomDto[];
    showingTags: ShowingTagDto[];
} & React.PropsWithChildren;

function Providers(props: ProvidersProps) {
    const { locale, messages, children, domainConfiguration, userAgent, cinemaRooms, showingTags } = props;
    const pulseConfig = useMemo(
        () => ({ endpoint: '/api/pulse/track', defaultContext: { cinema_id: domainConfiguration.cinema.id } }),
        [domainConfiguration.cinema.id]
    );
    const websiteConfiguration = domainConfiguration.websiteConfiguration;
    const googleAnalyticsMeasurementId = websiteConfiguration?.googleAnalyticsMeasurementId?.trim();
    const metaPixelId = websiteConfiguration?.metaPixelId?.trim();
    const hasMatomo = !!websiteConfiguration?.matomo?.trackerUrl?.trim() && !!websiteConfiguration?.matomo?.siteId;

    // Forwards every `useAnalytics().track()` event to the tracking systems the cinema connected in
    // the cinema app. The globals only exist once the matching tracker component has loaded its
    // script — which for GA4 and Meta only happens after consent — so the optional calls are the
    // consent gate as well.
    const trackers = useMemo(() => {
        const list: Array<(event: string, data?: Record<string, unknown>) => void> = [];

        if (googleAnalyticsMeasurementId) {
            list.push((event, data) => {
                window.gtag?.('event', GA4_EVENT_MAP[event] ?? toSnakeCase(event), {
                    ...data,
                    send_to: googleAnalyticsMeasurementId
                });
            });
        }

        if (metaPixelId) {
            list.push((event, data) => {
                const config = META_PIXEL_EVENT_MAP[event];
                if (config?.isStandard) {
                    window.fbq?.('track', config.eventName, config.mapParams?.(data ?? {}) ?? data);
                } else {
                    window.fbq?.('trackCustom', config?.eventName ?? event, data);
                }
            });
        }

        if (hasMatomo) {
            list.push((event, data) => {
                const config = MATOMO_EVENT_MAP[event];
                window._paq?.push([
                    'trackEvent',
                    config?.category ?? 'Cinema Website',
                    config?.action ?? event,
                    config?.mapName?.(data ?? {}) ?? ''
                ]);
            });
        }

        return list;
    }, [googleAnalyticsMeasurementId, metaPixelId, hasMatomo]);

    const [queryClient] = useState(
        () =>
            new QueryClient({
                defaultOptions: {
                    queries: {
                        // for SSR to avoid refetching immediately on the client
                        staleTime: 60 * 1000
                    }
                }
            })
    );

    return (
        <NextIntlClientProvider
            locale={locale}
            messages={messages}
            timeZone={COUNTRY_TIMEZONE_MAPPING[domainConfiguration.cinema.countryCode] || 'Europe/Berlin'}>
            <QueryClientProvider client={queryClient}>
                <CinemaRoomProvider cinemaRooms={cinemaRooms}>
                    <DomainConfigurationProvider domainConfiguration={domainConfiguration}>
                        <ShowingTagProvider showingTags={showingTags}>
                            <UserAgentProvider userAgent={userAgent}>
                                <AnalyticsProvider
                                    overallData={{
                                        cinemaId: domainConfiguration.cinema.id.toString(),
                                        cinemaName: domainConfiguration.cinema.name,
                                        domain: domainConfiguration.domain ?? ''
                                    }}
                                    trackers={trackers}>
                                    {/* PulseProvider must stay ABOVE AdvertisementProvider: the ad
                                        provider reports impressions/clicks to Pulse (CIN-14644) and
                                        usePulseTracker() throws outside this context, which would
                                        break every page rather than just lose the events. */}
                                    <PulseProvider config={pulseConfig}>
                                        <AdvertisementProvider>
                                            <NewsletterModalProvider cinemaId={domainConfiguration.cinema?.id}>
                                                {children}
                                                {/* Rendered once here so any bell on any page can
                                                    open it through the provider (CIN-13995). Must be
                                                    inside PulseProvider: the registration form it
                                                    renders calls usePulseTracker(). */}
                                                <NewsletterSubscribeModal />
                                            </NewsletterModalProvider>
                                            <ToastContainer
                                                position="top-right"
                                                autoClose={5000}
                                                closeOnClick
                                                pauseOnFocusLoss
                                                draggable
                                                pauseOnHover
                                                style={{ zIndex: 9999 }}
                                            />
                                        </AdvertisementProvider>
                                    </PulseProvider>
                                </AnalyticsProvider>
                            </UserAgentProvider>
                        </ShowingTagProvider>
                    </DomainConfigurationProvider>
                </CinemaRoomProvider>
            </QueryClientProvider>
        </NextIntlClientProvider>
    );
}

export default Providers;
